import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { useSEOConfig } from "@/lib/seo";
import { shouldShowOnboarding } from "@/hooks/usePermissions";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Hero from "@/components/home/Hero";
import Categories from "@/components/home/Categories";
import FeaturedProperties from "@/components/home/FeaturedProperties";


import BecomeHost from "@/components/home/BecomeHost";
import Testimonials from "@/components/home/Testimonials";
import PartnersSection from "@/components/home/PartnersSection";
import MobileHomeView from "@/components/home/MobileHomeView";
import { SEOHead } from "@/components/seo/SEOHead";
import AudioController from "@/components/layout/AudioController";
import CookieConsent from "@/components/layout/CookieConsent";

const Index = () => {
  const isMobile = useIsMobile();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const seoConfig = useSEOConfig();

  // Handle redirects based on device and auth state
  useEffect(() => {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    const isNative = Capacitor.isNativePlatform();
    const needsOnboarding = shouldShowOnboarding();

    // Only redirect to onboarding after sign-in (user exists) - mobile native only
    if (!loading && user && isNative && !isDesktop && needsOnboarding) {
      navigate("/onboarding", { replace: true, state: { from: "#/?" } });
      return;
    }

    // Don't redirect if we came from logout
    const fromLogout = location.state?.fromLogout;

    // Redirect non-authenticated mobile users to login (preserving any pending search intent)
    if (isMobile && !loading && !user) {
      import("@/lib/pendingSearch").then(({ readPendingSearch }) => {
        const pending = readPendingSearch();
        if (pending) {
          const target = `${pending.path}${pending.query || ""}`;
          navigate(`/auth?redirect=${encodeURIComponent(target)}`, { replace: true });
        } else {
          navigate("/auth");
        }
      });
      return;
    }

    // Desktop users (authenticated or not) stay on landing page
    // No redirect for desktop - landing page is accessible to all
  }, [isMobile, loading, user, navigate, location.state]);

  // Wait for mobile detection and auth before rendering
  if (isMobile === undefined || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Render mobile-optimized view for authenticated mobile users
  if (isMobile && user) {
    return <MobileHomeView />;
  }

  // Show nothing while redirecting non-authenticated mobile users
  if (isMobile && !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords?.join(", ")}
        canonical={seoConfig.canonical}
        type={seoConfig.ogType as "website" | "article" | "product"}
      />
      <Header />
      <AudioController />
      <CookieConsent />
      <main>
        <Hero />
        <Categories />
        <FeaturedProperties />
        
        
        
        <BecomeHost />
        <Testimonials />
        <PartnersSection />
      </main>
      <Footer showPaymentMethods />
    </div>
  );
};

export default Index;
